import React from 'react';

const About: React.FC = () => {
  return (
    <div className="pt-16 sm:pt-18 lg:pt-16 min-h-screen bg-cream">
      {/* Hero Header */}
      <div className="bg-gradient-to-b from-orange-50 to-cream py-10 sm:py-12 lg:py-16">
        <div className="container-base text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-pacifico text-orange-500 mb-2 sm:mb-3">
            Our Story
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-xl mx-auto px-4">
            From icy winters to warm community — meet the family behind Alljoy Donut Co.
          </p>
        </div>
      </div>

      {/* Founders Section */}
      <section className="section">
        <div className="container-base">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Image */}
            <div className="img-frame aspect-[4/3] shadow-elevated">
              <img
                src="/team.png"
                alt="Troy and Kristie Derda"
                className="w-full h-full object-cover object-center"
              />
            </div>

            {/* Content */}
            <div className="space-y-4 sm:space-y-6">
              <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                Welcome to Alljoy Donut Shop in Bluffton, South Carolina! We're Troy and Kristie Derda —
                parents, entrepreneurs, and adventurers who swapped the icy Midwest winters for a temperate
                life in the Lowcountry.
              </p>
              <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                Intrigued by this friendly community, we took a leap of faith, purchasing Alljoy from its
                retiring owners, Bill and Donna. Inheriting time-honored recipes and a dedicated team, we're
                committed to retaining the shop's charm while adding our unique touch.
              </p>
              <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                Kristie's best friend of 40 years, Peggy Faber, is our dedicated shop manager and enjoys life in
                Bluffton with her husband, Jerry.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="section bg-white">
        <div className="container-base">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Content */}
            <div className="order-2 lg:order-1">
              <h2 className="section-title text-left mb-4 sm:mb-6">Our Community</h2>
              <div className="space-y-4 sm:space-y-6">
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                  With a balance of perennial favorites like apple fritters and Reese's donuts, to creative
                  collaborations like our Burnt Church Distillery series, we've crafted something for every
                  palate.
                </p>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                  Beyond the kitchen, we've found joy in becoming active members of Bluffton — from music and
                  local festivals to paddleboarding and golf. Every donut is made with joy, and every customer
                  is family.
                </p>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed font-medium italic text-orange-600">
                  We can't wait to serve you a slice of our Lowcountry paradise.
                </p>
              </div>
            </div>

            {/* Image */}
            <div className="order-1 lg:order-2">
              <div className="img-frame aspect-[4/3] shadow-elevated">
                <img
                  src="/shop.jpg"
                  alt="Inside Alljoy Donut Shop"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-sm bg-cream">
        <div className="container-base">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                ),
                title: 'Made with Love',
                desc: 'Every donut is handcrafted with care and premium ingredients'
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                ),
                title: 'Family Owned',
                desc: 'A tight-knit team dedicated to serving our community'
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                ),
                title: 'Fresh Daily',
                desc: 'Baked fresh every morning for the best taste and quality'
              }
            ].map((item, i) => (
              <div key={i} className="card p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-500 flex items-center justify-center mx-auto mb-4">
                  {item.icon}
                </div>
                <h3 className="text-h4 text-gray-900 mb-2">{item.title}</h3>
                <p className="text-body-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
